import { SyntheticEvent } from 'react'
import {
    DEFAULT_CRITERIA_PARAMETER_NAME,
    parseSearchCriteria,
    SearchCriteria,
    SearchCriteriaFilterActions,
    serializeSearchCriteria
} from '@corratech/pylot-filters-and-sort/src/hooks/use-plp-manager'
import Image from '@corratech/pylot-image'
import { ConfigurableProductOptionsValues } from '@pylot-data/pylotschema'
import cn from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { ProductPrice } from '@components/common/ProductPrice/ProductPrice'
import s from './ProductTile.module.scss'
import { MagePlazaProductBadge } from '@corratech/pylot-mageplaza-badges'
import { getPDPUrl } from '@lib/getPDPUrl'
import { CartData } from '@corratech/pylot-cart-manager'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { useTranslation } from 'next-i18next'
import { AlfaRichContent as RichContent } from '@components/alfakher/pylot-rich-content'

import { getStoreConfig } from 'config'
import { YotpoStars } from '@components/YotpoStars/YotpoStars'
import AddToCartBtn from 'stores/ooka_usa_store/components/common/AddToCartBtn/AddToCartBtn'
import { useProductUI } from '@pylot-data/hooks/product/use-product-ui'
import type { ConfigurableVariant } from '@pylot-data/pylotschema'
import VariantSelector from '@components/alfakher/pylot-variant-selector/src/VariantSelector/VariantSelector'
import {
    buildAddToCartItemFromProductVariant,
    trackAddedToCartFromItems
} from '@components/Klaviyo/src/KlaviyoAddToCartTracker'
import { useUser } from '@corratech/pylot-auth-manager'

export interface ProductTileProps {
    product: any
    imageParam: {
        width?: number
    }
    className?: string
    isPDP?: boolean
    gtmLocation?: string
}
export interface Attribute {
    __typename: string
    code: string
    value_index: number
    label: string
}
export interface SmallImage {
    __typename: string
    url: string
}
export interface VariantProduct {
    __typename: string
    id: number
    small_image: SmallImage
    sku: string
    stock_status: string
}
export interface Variant {
    __typename: string
    attributes: Attribute[]
    product: VariantProduct
}
export const LoaderTile = () => (
    <div className={cn(s['productitem-block'])}>
        <Skeleton height={320} width={320} className={s['product-img']} />
        <Skeleton count={2} />
        <Skeleton circle width={16} count={2} />
    </div>
)

export const FwrdProductTile = ({
    product = {},
    imageParam = {},
    className,
    isPDP = false,
    gtmLocation
}: ProductTileProps): ReactElement | null => {
    const { openToast } = useUI()
    const { t } = useTranslation(['common', 'plp'])
    const { customer } = useUser()
    const { url_key } = product
    const isConfig = product.__typename === 'ConfigurableProduct'
    const { query } = useRouter()
    const search =
        typeof window !== 'undefined' && !isPDP
            ? new URL(window.location.href).search
            : ''
    const [urlSearchCriteria, setUrlSearchCriteria] = useState(search)
    const productUrl = getPDPUrl(url_key, isConfig ? urlSearchCriteria : '')
    const [selectedOption, setSelectedOption] = useState<SearchCriteria | null>(
        null
    )
    const [addToCartError, setAddToCartError] = useState<string | null>(null)

    const {
        setSelectedOptions,
        selectedOptions,
        options,
        variant,
        quantity,
        selectedOptionUIDs
    } = useProductUI(product)

    // TODO: make dynamic?
    const swatchAttr = 'color'
    const colorsImagesMap = new Map()
    const fullImageUrl = useCallback(
        (imageUrl?: string): string => {
            const widthString = imageParam.width
                ? `&width=${imageParam.width}`
                : ''
            return `${imageUrl}?format=auto&optimize=medium${widthString}`
        },
        [imageParam.width]
    )
    const [image, setImage] = useState(fullImageUrl(product.small_image?.url))

    useEffect(() => {
        if (!addToCartError) return

        const timeoutId = setTimeout(() => {
            setAddToCartError(null)
        }, 10000)
        return () => clearTimeout(timeoutId)
    }, [addToCartError])

    useEffect(() => {
        if (product?.variants?.length) {
            const targetVariant = product?.variants?.find(
                (variant: ConfigurableVariant) =>
                    variant.attributes?.find(
                        (attr) => attr!.code === 'ooka_color'
                    )
            )
            const targetAttribute = (targetVariant as ConfigurableVariant).attributes?.find(
                (attr) => attr!.code === 'ooka_color'
            )

            setSelectedOptions((state) => ({
                ...state,
                ooka_color: targetAttribute!.label as string
            }))
        }
    }, [product])

    useEffect(() => {
        const target = product?.variants?.find((variant: ConfigurableVariant) =>
            variant.attributes?.find(
                (attr) =>
                    attr!.code === 'ooka_color' &&
                    attr!.label === selectedOptions['ooka_color']
            )
        )
        if (target?.product?.thumbnail) {
            setImage(target?.product?.thumbnail?.url)
        } else if (target?.product?.small_image) {
            setImage(target?.product?.small_image?.url)
        }
    }, [selectedOptions, product])

    // During the changes in URL - convert new filters to product url
    useEffect(() => {
        if (!selectedOption) {
            setUrlSearchCriteria(search)
        }
        if (selectedOption) {
            onVariantChange({
                label: selectedOption.attribute_code,
                value_index: +selectedOption.filter_value
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        query,
        search,
        selectedOption?.attribute_code,
        selectedOption?.filter_value,
        urlSearchCriteria
    ])
    useEffect(() => {
        if (product.small_image?.url) {
            setImage(fullImageUrl(product.small_image?.url))
        }
    }, [fullImageUrl, product.small_image?.url])

    if (isConfig && product.variants) {
        product.variants.map((variant: Variant) => {
            const colorVariant = variant.attributes.find(
                (variant: { code: string }) => variant?.code === swatchAttr
            )
            const img = variant.product.small_image
                ? variant.product.small_image.url
                : false
            if (colorVariant && img) {
                colorsImagesMap.set(colorVariant.label, fullImageUrl(img))
            }
        })
    }
    const onVariantChange = ({
        label,
        value_index
    }: Partial<ConfigurableProductOptionsValues>) => {
        const value = value_index!.toString()
        const img = colorsImagesMap.get(label)
        if (img) {
            // setSelectedLabel(label!)
            setImage(img)
        }
        const searchCriteria = isPDP
            ? []
            : parseSearchCriteria(
                  query[DEFAULT_CRITERIA_PARAMETER_NAME] as string
              )
        const idxOfChangedAttribute = searchCriteria.findIndex(
            (sAttr) => sAttr.attribute_code === swatchAttr
        )
        let newOption: SearchCriteria
        if (idxOfChangedAttribute !== -1) {
            searchCriteria[idxOfChangedAttribute].filter_value = value
            newOption = searchCriteria[idxOfChangedAttribute]
        } else {
            newOption = {
                attribute_code: swatchAttr,
                filter_action: SearchCriteriaFilterActions.EQ,
                filter_value: value
            }
            searchCriteria.push(newOption)
        }
        const newSearchCriteriaString = `?${DEFAULT_CRITERIA_PARAMETER_NAME}=${serializeSearchCriteria(
            searchCriteria
        )}`
        setUrlSearchCriteria(newSearchCriteriaString)
        setSelectedOption(newOption)
    }

    const showAddToCartErrorMessage = (response: CartData | undefined) => {
        const errorMessages = response?.errors ?? response?.user_errors ?? null

        const errorCount = errorMessages?.length
        if (!errorCount) {
            const item = buildAddToCartItemFromProductVariant({
                product,
                variant,
                quantity,
                url: `${config.base.url.baseUrl}p/${product?.url_key}`
            })

            if (customer?.email) {
                trackAddedToCartFromItems([item], { email: customer.email })
            }
        }

        const allErrors = errorMessages
            ? errorMessages.map((errorMessage, i) => (
                  <>
                      <li key={i}>{errorMessage?.message}</li>
                      {errorCount !== i + 1 && <br />}
                  </>
              ))
            : ''
        if (allErrors.length) {
            openToast(<ul>{allErrors}</ul>, ToastType.Warning, true)
        }
    }

    const handleAddToCartInlineError = (message: string) => {
        if (!message) return

        const normalized = message.toLowerCase()
        const keyword = 'requested qty is not available'
        if (normalized.includes(keyword)) {
            return setAddToCartError('Maximum Stock Limit Reached')
        }
    }

    if (!product) return null

    const handleProductTileClickd = (e: SyntheticEvent<EventTarget>) => {
        const target = e.target as Element
        const promotionalElement = target.closest('.promotional')
        if (promotionalElement) {
            const res = promotionalElement.className.split(' ')
            const promotionalClassName = res[res.length - 1]
            localStorage.setItem('promotionBanner', promotionalClassName)
        } else {
            localStorage.removeItem('promotionBanner')
        }
    }
    if (product.__typename === 'custom_banner') {
        return (
            <div className={cn(s['productitem-block'])}>
                <img
                    src={product.imageSrc}
                    alt={t('custom kit')}
                    width="390"
                    height="390"
                    className={s['custom-image']}
                />
                <a href={product.customBannerUrl}>
                    <button className={s['custom-cta']}>
                        {t('GET STARTED')}
                    </button>
                </a>
            </div>
        )
    }
    const config = getStoreConfig()

    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
            className={cn(
                s['productitem-block'],
                className,
                'productitem-block'
            )}
            onClick={(e) => handleProductTileClickd(e)}
        >
            {addToCartError && (
                <div className={s['cart-toast']}>{addToCartError}</div>
            )}
            <div className={cn(s['product-eyebrow'], 'badge-info')}>
                {product.product_eyebrow_badge_text && (
                    <span
                        className={cn(
                            'default-badge',
                            product?.product_eyebrow_badge_text &&
                                String(
                                    product?.product_eyebrow_badge_text
                                ).toLowerCase()
                        )}
                    >
                        {product.product_eyebrow_badge_text}
                    </span>
                )}
            </div>
            <div className={cn('productimage-block')}>
                <div
                    className={cn(
                        s['productimg-link'],
                        'product-card-image-swipe',
                        'slider-product-img'
                    )}
                    aria-label={product.name}
                >
                    <div className={s['badges-container']}>
                        {product.mp_label_data && (
                            <MagePlazaProductBadge
                                labelData={product.mp_label_data}
                                showAllCustom
                            />
                        )}
                    </div>
                    <Link href={productUrl} prefetch={false}>
                        <a href={productUrl}>
                            <Image
                                src={image}
                                alt={
                                    product.name ? product.name : 'placeholder'
                                }
                                width={390}
                                height={390}
                                className={s['product-img']}
                            />
                        </a>
                    </Link>
                </div>
            </div>
            <div className={cn(s['plp-prod-details'], 'sliderproduct-detail')}>
                <Link href={productUrl} prefetch={false}>
                    <div>
                        <strong
                            className={cn(
                                s['product-brand'],
                                'product-brand-name'
                            )}
                        >
                            {product.brand_text !== 'false'
                                ? product.brand_text
                                : ''}
                        </strong>
                        <a
                            className={(s['product-link'], 'product-link')}
                            href={productUrl}
                        >
                            <h5
                                className={cn(
                                    s['product-name'],
                                    'product-name uppercase text-sm leading-extra-4.4 md:text-lg font-medium tracking-extra-base cursor-pointer md:leading-5'
                                )}
                                title={product.name}
                            >
                                {product.name}
                            </h5>
                        </a>
                        <p className={cn(s['product-subtitle'], 'subtitle')}>
                            {product?.subtitle}
                        </p>
                        <p className={cn(s['short_description'], 'shortDesc')}>
                            <RichContent
                                html={product.short_description?.html}
                            />
                        </p>
                        <div>
                            <YotpoStars
                                productId={product?.id}
                                yotoApiKey={config.base.yotpo.api_key}
                            />
                        </div>
                    </div>
                </Link>

                {options.map((opt, idx: number) => {
                    return (
                        <div
                            className={cn(
                                {
                                    ['pb-4']: idx !== options.length - 1
                                },
                                s['plp-swatches'],
                                'plp-swatches'
                            )}
                            key={opt.code}
                        >
                            <VariantSelector
                                disableAll={
                                    product.stock_status === 'OUT_OF_STOCK'
                                }
                                variants={product.variants}
                                options={options}
                                selectedOptions={selectedOptions}
                                setSelectedOptions={setSelectedOptions}
                                displaySwatchOptionLabel
                                isCentered
                            />
                        </div>
                    )
                })}
            </div>
            <div className={cn('product-info')}>
                <div className={(s['product-details'], 'text-center')}>
                    <ProductPrice
                        className={cn(
                            String(product.__typename).toLowerCase(),
                            'plp'
                        )}
                        priceRange={product.price_range}
                        productType={product.__typename}
                        showDiscountPrice={!product?.is_bulk_product}
                        priceText
                    />
                </div>
            </div>
            <div className={s['plp-add-to-cart']}>
                <AddToCartBtn
                    inViewport
                    product={product}
                    onResponse={showAddToCartErrorMessage}
                    onError={handleAddToCartInlineError}
                    gtmLocation={gtmLocation}
                    selectedOptionUIDs={selectedOptionUIDs}
                    isPlp
                />
            </div>
        </div>
    )
}
export const ProductItem = React.memo(
    (props: ProductTileProps) => <FwrdProductTile {...props} />,
    (prevProps, newProps) =>
        JSON.stringify(prevProps) === JSON.stringify(newProps)
)
ProductItem.displayName = 'MemoProductItem'
